import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from './firebase';

export interface DeviceTrackingSession {
  userId: string;
  deviceId?: string;
  productFlavor?: string;
  billingPeriod?: string;
  timestamp?: Date;
  // Add other fields as they exist in your Firestore schema
  sessionId?: string;
  appVersion?: string;
  deviceModel?: string;
  osVersion?: string;
}

/**
 * Fetch device tracking sessions for a specific billing period and product flavor
 * @param billingPeriod - Billing period in YYYY-MM format (e.g., "2025-10")
 * @param productFlavor - Optional product flavor filter
 */
export const fetchDeviceTrackingSessions = async (
  billingPeriod: string,
  productFlavor?: string
): Promise<DeviceTrackingSession[]> => {
  try {
    console.log(`Fetching device tracking for billing period: ${billingPeriod}`, {
      productFlavor
    });

    // Build the query
    const deviceTrackingRef = collection(firestore, 'device_tracking');

    // Build query with billingPeriod filter
    let q = query(
      deviceTrackingRef,
      where('billingPeriod', '==', billingPeriod)
    );

    // Add product flavor filter if specified
    if (productFlavor && productFlavor !== 'all') {
      q = query(
        deviceTrackingRef,
        where('billingPeriod', '==', billingPeriod),
        where('productFlavor', '==', productFlavor)
      );
    }

    const querySnapshot = await getDocs(q);

    const sessions: DeviceTrackingSession[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      sessions.push({
        userId: data.userId || data.uid || 'unknown',
        deviceId: data.deviceId,
        productFlavor: data.productFlavor,
        billingPeriod: data.billingPeriod,
        timestamp: data.timestamp?.toDate?.() || undefined,
        sessionId: data.sessionId,
        appVersion: data.appVersion,
        deviceModel: data.deviceModel,
        osVersion: data.osVersion,
      });
    });

    console.log(`Fetched ${sessions.length} sessions`);
    return sessions;
  } catch (error) {
    console.error('Error fetching device tracking sessions:', error);
    throw error;
  }
};

/**
 * Get unique user IDs from sessions
 */
export const getUniqueUserIds = (sessions: DeviceTrackingSession[]): string[] => {
  const uniqueIds = new Set<string>();
  sessions.forEach(session => {
    if (session.userId && session.userId !== 'unknown') {
      uniqueIds.add(session.userId);
    }
  });
  return Array.from(uniqueIds).sort();
};

/**
 * Get available product flavors from sessions
 */
export const getProductFlavors = (sessions: DeviceTrackingSession[]): string[] => {
  const flavors = new Set<string>();
  sessions.forEach(session => {
    if (session.productFlavor) {
      flavors.add(session.productFlavor);
    }
  });
  return Array.from(flavors).sort();
};
